import { Button } from '../../components/Button';
import { Counter } from '../../components/Counter';
import { InfoNote } from '../../components/InfoNote';
import { NotificationBox } from '../../components/NotificationBox';
import { PageHeading } from '../../components/PageHeading';
import { PanelCard } from '../../components/PanelCard';
import { FromPriceDisplay } from '../../components/PriceDisplay';
import { ReservationCalendar } from '../../components/ReservationCalendar';
import { SectionHeader } from '../../components/SectionHeader';
import { SelectablePill } from '../../components/SelectablePill';
import { TimeChangeIcon } from '../../icons/TimeChangeIcon';
import {
  CalendarSection,
  Eyebrow,
  FromPriceRow,
  LimitNote,
  OptionSection,
  PerPlayer,
  PillGroup,
  PlayerControl,
  PlayerLabel,
  PlayerRow,
  PriceHeader,
  ScrollContainer,
} from './GolfBookingContent.styles';
import type { GolfBookingContentProps } from './GolfBookingContent.types';

const defaultNoteIcon = <TimeChangeIcon size={18} />;

export function GolfBookingContent({
  title,
  subtitle,
  courseTypeLabel,
  fromPrice,
  unavailable,
  onUnavailableCtaClick,
  onBack,
  playerCount,
  minPlayers = 1,
  maxPlayers,
  playerLimitNote,
  onPlayerCountChange,
  activeDate,
  selectedDate,
  calendarView,
  availability,
  onDateSelect,
  onWeekNavigate,
  onCalendarViewChange,
  calendarLabels,
  holesOptions = [],
  selectedHoles,
  onHolesSelect,
  timeBandOptions = [],
  selectedTimeBand,
  onTimeBandSelect,
  onViewTeeTimes,
  viewTeeTimesDisabled,
  locale,
  noteText,
  noteIcon = defaultNoteIcon,
  labels,
}: GolfBookingContentProps) {
  const playersLabel = labels?.playersLabel ?? 'Players';
  const holesLabel = labels?.holesLabel ?? 'Number of holes';
  const timeBandLabel = labels?.timeBandLabel ?? 'Preferred time';

  const showHoles = holesOptions.length > 0 && !!onHolesSelect;
  const showTimeBands = timeBandOptions.length > 0 && !!onTimeBandSelect;
  const unavailableNotice = unavailable
    ? {
        title: labels?.unavailable?.title ?? 'Unavailable',
        subtitle: labels?.unavailable?.subtitle ?? 'No tee times are available for this selection.',
        ctaLabel: labels?.unavailable?.ctaLabel,
      }
    : undefined;

  return (
    <ScrollContainer>
      <PageHeading title={title} subtitle={subtitle} />

      {(courseTypeLabel || fromPrice) && (
        <PriceHeader>
          {courseTypeLabel && <Eyebrow>{courseTypeLabel}</Eyebrow>}
          {fromPrice && (
            <FromPriceRow>
              <FromPriceDisplay
                amount={fromPrice.amount}
                currencyCode={fromPrice.currencyCode}
                label={labels?.fromPriceLabel}
                locale={locale}
                size="large"
                noPennies
              />
              <PerPlayer>{labels?.perPlayerLabel ?? 'per player'}</PerPlayer>
            </FromPriceRow>
          )}
        </PriceHeader>
      )}

      <PanelCard>
        <SectionHeader
          title={labels?.reservation?.title ?? 'Your round.'}
          subtitle={labels?.reservation?.subtitle}
        />

        <PlayerRow>
          <PlayerLabel>{playersLabel}</PlayerLabel>
          <PlayerControl>
            {playerLimitNote && <LimitNote>{playerLimitNote}</LimitNote>}
            <Counter
              value={playerCount}
              min={minPlayers}
              max={maxPlayers}
              onChange={onPlayerCountChange}
              decreaseLabel={labels?.counter?.decrease}
              increaseLabel={labels?.counter?.increase}
            />
          </PlayerControl>
        </PlayerRow>

        <CalendarSection>
          <ReservationCalendar
            activeDate={activeDate}
            selectedDate={selectedDate}
            view={calendarView}
            availability={availability}
            onDateSelect={onDateSelect}
            onNavigate={onWeekNavigate}
            onViewChange={onCalendarViewChange}
            labels={calendarLabels}
          />
        </CalendarSection>

        {showHoles && (
          <OptionSection>
            <SectionHeader title={holesLabel} />
            <PillGroup role="group" aria-label={holesLabel}>
              {holesOptions.map((option) => (
                <SelectablePill
                  key={option.value}
                  label={option.label}
                  selected={option.value === selectedHoles}
                  onClick={() => onHolesSelect(option.value)}
                />
              ))}
            </PillGroup>
          </OptionSection>
        )}

        {showTimeBands && (
          <OptionSection>
            <SectionHeader title={timeBandLabel} />
            <PillGroup role="group" aria-label={timeBandLabel}>
              {timeBandOptions.map((option) => (
                <SelectablePill
                  key={option.value}
                  label={option.label}
                  selected={option.value === selectedTimeBand}
                  disabled={option.disabled}
                  onClick={() => onTimeBandSelect(option.value)}
                />
              ))}
            </PillGroup>
          </OptionSection>
        )}

        {noteText && <InfoNote text={noteText} icon={noteIcon} />}
      </PanelCard>

      {unavailableNotice ? (
        <NotificationBox
          title={unavailableNotice.title}
          subtitle={unavailableNotice.subtitle}
          cta={
            unavailableNotice.ctaLabel && (
              <Button
                type="button"
                variant="primary"
                size="large"
                onClick={onUnavailableCtaClick ?? onBack}
                disabled={!onUnavailableCtaClick && !onBack}
              >
                {unavailableNotice.ctaLabel}
              </Button>
            )
          }
        />
      ) : (
        onViewTeeTimes && (
          <Button
            type="button"
            variant="primary"
            size="large"
            fullWidth
            onClick={onViewTeeTimes}
            disabled={viewTeeTimesDisabled}
          >
            {labels?.viewTeeTimesLabel ?? 'View tee times'}
          </Button>
        )
      )}
    </ScrollContainer>
  );
}
